// V70.67 — İK Portalı sayfa grupları: portal sidebar'ında grup başına TEK satır,
// grup sayfalarının üstünde grubun renginde yatay sekme şeridi (HRGroupTabs).
//
// V70.179 — spec v1.1 §0.3 / §P0.5: menü artık burada HARDCODE EDİLMİYOR.
// Tek source of truth `hrSiteRegistry.json`; bu dosya yalnız o registry'den menü
// görünümünü türetir ve yetki kurallarını uygular.
//
// V70.182 — spec §3.2 TO-BE uygulandı: gruplar ÜÇ bölüme ayrıldı.
//   kind:'self'    → Portalım tarafı: kişinin kendi kayıtları
//   kind:'operate' → Operasyon: günlük işlemlerin yapıldığı ekranlar
//   kind:'manage'  → Yönetim: tanım / politika / limit / kural / sistem ayarı
//
// YETKİ SEMANTİĞİ DEĞİŞMEDİ (V70.55): 'operate' de 'manage' ile aynı kapıyı kullanır —
// hr_yonetim genel anahtarı ikisini de açar. Bölünme yalnız GÖRSEL gruplamadır;
// aksi halde mevcut kullanıcıların erişimi sessizce daralırdı.
//
// V71 — yeni yetki motoru (Kullanıcı & Yetki ekranında verilen rol/set/vekalet/istisna)
// menüye ve portal rotalarına yansır: legacy anahtar VEYA motordaki açık atama.
// `engine` parametresi opsiyoneldir ({ ready, canExplicit } — hooks/useHrPermissions).

import { hrMenuGroups, hrNode, hrNodeForRoute } from './hrSiteRegistry';
import { menuPermissionOf } from './hrPermissionDecode';

export const HR_GROUPS = hrMenuGroups();

const itemMatches = (i, pathname) => pathname === i.to || pathname.startsWith(i.to + '/');

// V71 — menüde olmayan alt yol (/HR/staff/:id) registry node'unun grubuna bağlanır
export const findHRGroupForPath = (pathname) => {
  const direct = HR_GROUPS.find(g => g.items.some(i => itemMatches(i, pathname)));
  if (direct) return direct;
  const node = hrNodeForRoute(pathname);
  return (node && node.group && HR_GROUPS.find(g => g.key === node.group)) || null;
};

/** Yoldaki aktif menü öğesi (sekme etiketi / kırıntı için). */
export const findHRItemForPath = (pathname) => {
  for (const g of HR_GROUPS) {
    const item = g.items.find(i => itemMatches(i, pathname));
    if (item) return item;
  }
  return null;
};

// V70.55 yetki kuralları: genel anahtar kategorinin tamamını, alt anahtar tek sayfayı açar
const legacyCanSee = (item, kind, hasPermission, isAdmin) => {
  if (isAdmin) return true;
  if (kind === 'manage' || kind === 'operate') {
    // V70.194 — permRequired burada da geçerli: genel hr_yonetim anahtarının AÇMADIĞI
    // yönetim sayfaları var. Kullanıcı & Yetki Yönetimi böyle — yetki dağıtma hakkı
    // İK yöneticiliğinden türemez (spec §2.4), backend de management.permission ister.
    // Kendi anahtarı olmayan permRequired sayfa yalnız platform yöneticisine görünür.
    if (item.permRequired) return !!(item.perm && hasPermission(item.perm, 'view'));
    return hasPermission('hr_yonetim', 'view') || !!(item.perm && hasPermission(item.perm, 'view'));
  }
  // self: genel hr_modul anahtarı kategoriyi açar — ancak permRequired işaretli sayfa
  // (arka ucu hâlâ yönetim yetkisi isteyen self-servis ekranı) yalnız kendi anahtarıyla açılır
  if (item.permRequired) return !!(item.perm && hasPermission(item.perm, 'view'));
  return hasPermission('hr_modul', 'view') || (item.perm ? hasPermission(item.perm, 'view') : true);
};

// Backend hâlâ yalnız legacy anahtar kontrol ediyor (weekly-duty.js, hr-registry.js /registry) — motor açmasın
const ENGINE_EXCLUDED = new Set(['hr.weekly_duty', 'hr.site_map']);

// V71 — motor sonucu: yalnız açık atamalar; yönetim/operasyon ekranı için kapsam 'self'ten geniş olmalı
const engineCanSee = (engine, item, kind) => {
  if (!engine || !engine.ready || typeof engine.canExplicit !== 'function') return false;
  if (ENGINE_EXCLUDED.has(item.id)) return false;
  const p = menuPermissionOf(hrNode(item.id));
  if (!p) return false;
  return engine.canExplicit(p.resource, p.action, kind !== 'self') === true;
};

export const canSeeHRItem = (item, kind, hasPermission, isAdmin, engine) =>
  legacyCanSee(item, kind, hasPermission, isAdmin) || engineCanSee(engine, item, kind);

export const visibleHRGroupItems = (group, hasPermission, isAdmin, engine) =>
  group.items.filter(i => canSeeHRItem(i, group.kind, hasPermission, isAdmin, engine));

// V71 — TEK KARAR NOKTASI (hub getVisibleGroup karşılığı): şerit (HRGroupTabs) ve
// içerik kabındaki .pg-grouped aynı sonucu kullanır; görünür sekme <2 ise şerit yok.
export const getVisibleHRGroup = (pathname, hasPermission, isAdmin, engine) => {
  const group = findHRGroupForPath(pathname);
  if (!group) return null;
  const visible = visibleHRGroupItems(group, hasPermission, isAdmin, engine);
  return { group, visible, showTabs: visible.length >= 2 };
};

// V70.55 portala giriş semantiği — V71: tek yerde (HRLayout kapısı + hub'daki İK Portalı CTA'sı).
// Genel hr_modul ya da görünür en az bir yetkili self sayfası ya da operasyon/yönetim sayfası.
export const canEnterHRPortal = (hasPermission, isAdmin, engine) => {
  if (isAdmin || hasPermission('hr_modul', 'view')) return true;
  return HR_GROUPS.some(g => visibleHRGroupItems(g, hasPermission, isAdmin, engine)
    .some(i => g.kind !== 'self' || !!i.perm));
};

// V71 — portal içi rota koruması: menüde gizli sayfa URL ile de açılmaz.
// Menüde karşılığı olmayan alt sayfa (/HR/staff/:id) grubundan en az bir sayfa görünüyorsa açılır.
// Registry'de olmayan yol serbest (bilinmeyen adresi '*' rotası zaten panele yönlendirir).
// Self-servis talep sayfaları: portala giren herkes kendi talebini açar (backend builtin:self)
const SELF_OPEN_ROUTES = new Set(['hr.leaves', 'hr.advances', 'hr.expenses', 'hr.eval_tasks']);
export const canSeeHRRoute = (pathname, hasPermission, isAdmin, engine) => {
  if (isAdmin) return true;
  const node = hrNodeForRoute(String(pathname || '').split('?')[0]);
  if (!node) return true;
  for (const g of HR_GROUPS) {
    const item = g.items.find(i => i.id === node.id);
    // V71 — menüde gizli ama davetle açılan self sayfa (Değerlendirme Görevlerim): bildirim linki
    // çalışmaya devam etsin; liste yalnız kişinin kendi görevleri, arka uç ayrıca denetler
    if (item && g.kind === 'self' && (item.permRequired || SELF_OPEN_ROUTES.has(item.id))) return true;
    if (item) return canSeeHRItem(item, g.kind, hasPermission, isAdmin, engine);
  }
  const group = node.group && HR_GROUPS.find(g => g.key === node.group);
  if (group) return visibleHRGroupItems(group, hasPermission, isAdmin, engine).length > 0;
  return true;
};

// V71 — personel kaydı düzenleme: Çalışanlar menü kuralıyla aynı anahtarlar (+ eski genel İK anahtarı)
export const canEditHRStaff = (hasPermission, isAdmin) =>
  isAdmin || ['hr_yonetim', 'hr_yonetim_orgchart', 'hr_performance'].some(m => hasPermission(m, 'create'));
